import { DEFAULT_NEW_PAGE_SLUG_RANDOM_LENGTH, MAX_SLUG_RETRY } from '@/constants';
import { useUpdateRoutesMutation } from '@/store';
import { ISpaceRoute, errToString, validUUID } from '@typedream/data';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import { useEffect, useRef, useState } from 'react';
import { DropTargetMonitor, XYCoord, useDrag, useDrop } from 'react-dnd';
import { toast } from 'react-toastify';
import { DROP, DragItemPage, PAGE_TYPE, PagesDnDWrapperProps } from './types';

function getHoverPageDirection(
  dragPath: string[],
  dropPath: string[],
  dropRef: any,
  monitor: DropTargetMonitor
): DROP | null {
  // Cannot delete home page
  if (dragPath.length <= 0) {
    return null;
  }
  // Cannot move to it's current path or inside it's own children
  if (isEqual(dragPath, dropPath) || isEqual(dragPath, dropPath.slice(0, dragPath.length))) {
    return null;
  }

  // Determine rectangle on screen
  const hoverBoundingRect = dropRef.current?.getBoundingClientRect();

  //    ===================
  //    |                 |
  //    ----- Top Cut -----  (25%) = ↑ Put on top
  //    |                 |          ↓
  //    ..      Mid      ..          Put inside
  //    |                 |          ↑
  //    ----- Bot Cut -----  (75%) = ↓ Put on below
  //    |                 |
  //    ===================

  // Get top cutoff
  const hoverTopCutoff = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 4;
  // Get bottom cutoff
  const hoverBottomCutoff = (hoverBoundingRect.bottom - hoverBoundingRect.top) * (3 / 4);

  // Determine mouse position
  const clientOffset = monitor.getClientOffset();
  if (!clientOffset) return null;

  // Get pixels to the top
  const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

  // Dragging upwards
  if (hoverClientY < hoverTopCutoff) {
    return DROP.TOP;
  }
  // Dragging downwards
  if (hoverClientY > hoverBottomCutoff) {
    return DROP.BOTTOM;
  }
  // Dragging into
  return DROP.INSIDE;
}

function deletePath(
  route: ISpaceRoute,
  path: string[]
): { newRoute: ISpaceRoute; deletedRoute: ISpaceRoute } {
  // Cannot delete home page
  if (path.length <= 0) {
    return { newRoute: route, deletedRoute: null };
  }

  // 1. Get the contents of path in the route
  // clone the route so when we delete stuff it doesn't modify original route
  const copyRoute = JSON.parse(JSON.stringify(route));

  let currRoute = copyRoute;
  let deletedRoute: ISpaceRoute; // The route that we're going to move
  const slugToMove = path[path.length - 1];
  let count = 0;
  const maxCount = path.length;
  while (path.length >= 0 && count <= maxCount) {
    const currPath = path.shift();
    if (currRoute.children && currRoute.children[currPath]) {
      if (currPath === slugToMove && path.length <= 0) {
        // 2. Remove the path from the route
        deletedRoute = currRoute.children[currPath];
        delete currRoute.children[currPath];
        // Remove path from children_keys
        const idx = currRoute.children_keys.indexOf(slugToMove);
        if (idx > -1) {
          currRoute.children_keys.splice(idx, 1);
        }
        break;
      } else {
        currRoute = currRoute.children[currPath];
      }
    }
    count++;
  }
  return { newRoute: copyRoute, deletedRoute };
}

function insertPath(
  route: ISpaceRoute,
  path: string[],
  routeToInsert: ISpaceRoute,
  slugToInsert: string,
  direction: DROP,
  directionPathSlug: string
): ISpaceRoute {
  // 1. Get the contents of path in the route
  // clone the route so when we delete stuff it doesn't modify original route
  const copyRoute = JSON.parse(JSON.stringify(route));
  const originalPath = JSON.parse(JSON.stringify(path));

  let currRoute = copyRoute;
  let count = 0;
  const maxCount = path.length;
  while (path.length >= 0 && count <= maxCount) {
    // We have arrived at the route to insert
    if (path.length === 0) {
      if (currRoute.children_template_page_id && validUUID(currRoute.children_template_page_id)) {
        throw new Error(
          `Error creating page, the path "/${originalPath?.join(
            '/'
          )}" is used for the children of a collection`
        );
      }
      if (!currRoute.children) {
        currRoute.children = {};
      }
      if (!currRoute.children_keys) {
        currRoute.children_keys = [];
      }

      // if slugToInsert already exist in this path, add
      // unique string
      let newPageSlug = slugToInsert;
      let ct = 0;
      while (Object.keys(currRoute.children).includes(slugToInsert) && ct < MAX_SLUG_RETRY) {
        const randomSuffix = Math.random()
          .toString(36)
          .substring(5, 5 + DEFAULT_NEW_PAGE_SLUG_RANDOM_LENGTH);
        newPageSlug = `${newPageSlug}-${randomSuffix}`;
        ct += 1;
      }

      if (Object.keys(currRoute.children).includes(newPageSlug)) {
        throw new Error('Error creating page, unable to make a random slug for your new page');
      }

      // Add route to children
      currRoute.children[newPageSlug] = routeToInsert;

      if (direction === DROP.TOP) {
        const directionPathSlugIdx = currRoute.children_keys.indexOf(directionPathSlug);
        if (directionPathSlugIdx > -1) {
          // if idx found, insert slug at index and delete 0 items
          currRoute.children_keys.splice(directionPathSlugIdx, 0, newPageSlug);
          break;
        }
      }
      if (direction === DROP.BOTTOM) {
        const directionPathSlugIdx = currRoute.children_keys.indexOf(directionPathSlug);
        if (directionPathSlugIdx > -1) {
          // if idx found, insert slug at index+1 and delete 0 items
          currRoute.children_keys.splice(directionPathSlugIdx + 1, 0, newPageSlug);
          break;
        }
      }
      // if no direction specified, just push to end of array
      currRoute.children_keys.push(newPageSlug);
      break;
    } else {
      const currPath = path.shift();
      currRoute = currRoute.children[currPath];
    }
    count++;
  }
  return copyRoute;
}

// originalPath includes the slug we're moving
// newPath doesn't include the slug we're moving
function movePath(
  route: ISpaceRoute,
  originalPath: string[],
  newPath: string[],
  direction: DROP
): ISpaceRoute {
  // Cannot delete home page
  if (originalPath.length <= 0) {
    return route;
  }
  // Cannot move to it's current path or inside it's own children
  if (
    isEqual(originalPath, newPath) ||
    isEqual(originalPath, newPath.slice(0, originalPath.length))
  ) {
    return route;
  }

  let directionPathSlug = null;

  // Dropping on home page
  if (newPath.length <= 0) {
    // Cannot drop on home page if home page has no children
    // (cos then who was dropped there?)
    if (route.children_keys?.length <= 0) {
      return route;
    }

    // Dropping inside the home page or below the home page doesn't do anything
    if (direction === DROP.BOTTOM || direction === DROP.INSIDE) {
      return route;
    }
    // Dropping on top of the home page makes the dragged page a home page
    // and moved the home page as the first child

    // 1. Get the original home page id
    const homePageID = route.page_id;
    // 2. Get the contents of originalPath in the route
    const { newRoute, deletedRoute } = deletePath(route, originalPath);

    if (
      deletedRoute?.children_template_page_id &&
      validUUID(deletedRoute?.children_template_page_id)
    ) {
      throw new Error(
        'Cannot use the Collection Parent Page as your Home Page. Please try other pages.'
      );
    }

    // If the dragged page has children, don't allow to make it a home page
    // because we don't know what to do with the children...
    if (deletedRoute.children && Object.keys(deletedRoute.children)?.length > 0) {
      throw new Error('Please remove all subpages inside this page before making it a home page.');
    }

    // 3. Make the deleted route as the home page
    newRoute.page_id = deletedRoute.page_id;
    // 4. Insert the current home page as the first child
    const homePageRoute: ISpaceRoute = {
      page_id: homePageID,
    };
    return insertPath(newRoute, [], homePageRoute, 'home', DROP.TOP, route.children_keys[0]);
  }

  // Dropping on non homepage
  // if direction is top or bottom, then don't move into the current path,
  // instead move to its parent
  let newPathToInsert = newPath;
  if (direction === DROP.TOP || direction === DROP.BOTTOM) {
    // directionPathSlug is the drop path's slug,
    // we'll use this to find the index before or after based on direction
    directionPathSlug = newPath[newPath.length - 1];
    newPathToInsert = newPath.slice(0, -1);
  }
  // 1. Get the contents of originalPath in the route
  const slugToMove = originalPath[originalPath.length - 1];
  const { newRoute, deletedRoute } = deletePath(route, originalPath);
  // 2. Insert deletedRoute to the new path
  return insertPath(
    newRoute,
    newPathToInsert,
    deletedRoute,
    slugToMove,
    direction,
    directionPathSlug
  );
}

/**
 * Wrapper for `SiebarPageTreeItem` to handle drag and drop
 */
export function PagesDnDWrapper({
  space,
  path, // path includes the current page as the last slug
  children,
  setdropDirection,
  setisOverCurrent,
  open,
  setopen,
  pageTreeRole,
}: PagesDnDWrapperProps) {
  const dropRef = useRef<HTMLDivElement>(null);
  const [isFolderOpen, setIsOpenFolder] = useState(false);

  const openFolderRef = useRef(isFolderOpen);
  const setOpenFolder = (data) => {
    openFolderRef.current = data;
    setIsOpenFolder(data);
  };

  const [{ isDragging }, drag] = useDrag({
    type: PAGE_TYPE,
    item: { path },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const [{ isOver, isOverCurrent }, drop] = useDrop({
    accept: PAGE_TYPE,
    drop: async (item: DragItemPage, monitor) => {
      // didDrop means it's been drop to a child
      const didDrop = monitor.didDrop();
      if (didDrop) {
        return;
      }
      const direction = getHoverPageDirection(item.path, path, dropRef, monitor);

      try {
        const newRoutes = movePath(space.routes, item.path, path, direction);
        await useUpdateRoutesMutation.run([{ spaceID: space?.id, routes: newRoutes }]).unwrap();
      } catch (e) {
        toast.error(errToString(e));
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      isOverCurrent: !!monitor.isOver({ shallow: true }),
    }),
    hover(item: DragItemPage, monitor: DropTargetMonitor) {
      const direction = getHoverPageDirection(item.path, path, dropRef, monitor);
      setdropDirection(direction);
    },
  });

  drop(dropRef);

  useEffect(() => {
    if (isOverCurrent) {
      setisOverCurrent(true);

      // If still hovering for 1 second, open this folder
      if (!open) {
        setOpenFolder(true);
        setTimeout(() => {
          if (openFolderRef.current) {
            setopen(true);
          }
        }, 1000);
      }
    } else {
      // If was hovering but not anymore, cancel opening the folder
      if (isFolderOpen) {
        setOpenFolder(false);
      }
      setisOverCurrent(false);
    }

    if (!isOver) {
      setdropDirection(null);
    }
  }, [isOver, isOverCurrent]);

  return (
    <div
      ref={dropRef}
      className={classNames({
        'ml-0': pageTreeRole === 'root-page',
        'ml-[24px]': pageTreeRole === 'child-page',
      })}
    >
      <div
        data-testid="DragAndDropPage_drag_container"
        ref={drag}
        style={{
          opacity: isDragging ? 0.5 : 1,
          cursor: 'move',
        }}
      >
        {children}
      </div>
    </div>
  );
}
